import { useMemo } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { getCategoryHexColor } from "@/utils/categoryColors";
import { Project } from "@/types";
import { CategoryType } from "@/types/category";
import { useProjectsBatchData } from "@/hooks/useProjectsBatchData";

interface CategoryData {
  category: CategoryType;
  displayName: string;
  description: string;
  totalMeasuresCount: number;
  color: string;
}

interface UseCategoryDataProps {
  projects: Project[];
  projectToBatchIds: Record<string, string[]>;
  batchTitles: Record<string, string>;
  selectedCategory?: CategoryType;
  selectedProjectId?: string;
}

export const useCategoryData = ({
  projects,
  projectToBatchIds,
  batchTitles,
  selectedCategory,
  selectedProjectId,
}: UseCategoryDataProps): {
  categoryData: CategoryData[];
  projectsMeasurements: Record<string, any>;
  filteredProjects: Project[];
} => {
  const { t } = useLanguage();

  // Get batch data for all projects
  const { projectsMeasurements } = useProjectsBatchData(
    projects,
    projectToBatchIds,
    batchTitles
  );

  // Calculate category data across all projects
  return useMemo(() => {
    const implementedCounts: Record<CategoryType, number> = {} as Record<
      CategoryType,
      number
    >;
    const plannedCounts: Record<CategoryType, number> = {} as Record<
      CategoryType,
      number
    >;
    const allCategories = new Set<CategoryType>();

    // Filter projects based on selected project
    const filteredProjects = selectedProjectId
      ? projects.filter((project) => project.id === selectedProjectId)
      : projects;

    // Aggregate measures across filtered projects
    filteredProjects.forEach((project) => {
      const measurements = projectsMeasurements[project.id];
      if (!measurements) return;

      const { planned, documented } = measurements;

      // Count documented (implemented) measures
      if (documented.length > 0) {
        const byCategory = documented.reduce((acc, item) => {
          const cat = item.category;
          if (!acc[cat]) acc[cat] = [];
          acc[cat].push(item);
          return acc;
        }, {} as Record<string, any[]>);

        for (const cat of Object.keys(byCategory)) {
          // Apply category filter
          if (selectedCategory && cat !== selectedCategory) continue;

          allCategories.add(cat as CategoryType);
          const catArr = byCategory[cat];
          implementedCounts[cat as CategoryType] =
            (implementedCounts[cat as CategoryType] || 0) + catArr.length;
        }
      }

      // Count planned measures
      if (planned.length > 0) {
        for (const measure of planned) {
          if (!measure.category) continue;

          // Apply category filter
          if (selectedCategory && measure.category !== selectedCategory)
            continue;

          allCategories.add(measure.category as CategoryType);
          plannedCounts[measure.category as CategoryType] =
            (plannedCounts[measure.category as CategoryType] || 0) + 1;
        }
      }
    });

    // Convert to array and sort by total count (descending)
    const categoryData = Array.from(allCategories)
      .map((category) => {
        const implementedCount = implementedCounts[category] || 0;
        const plannedCount = plannedCounts[category] || 0;
        const totalCount = implementedCount + plannedCount;

        return {
          category,
          displayName: t(`categories.${category}`),
          description: t(`categories.descriptions.${category}`),
          totalMeasuresCount: totalCount,
          color: getCategoryHexColor(category),
        };
      })
      .sort((a, b) => b.totalMeasuresCount - a.totalMeasuresCount);

    return { categoryData, projectsMeasurements, filteredProjects };
  }, [projects, projectsMeasurements, t, selectedCategory, selectedProjectId]);
};
